import { useEffect } from "react"

import { Button, Flex, Modal, Spin, Typography } from "antd"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import { MATCH_TYPE_LABEL, STATUS_META } from "../../constants/constants"
import { selectStatusLogs } from "../../redux/business.slice"
import { getStatusLogs } from "../../redux/business.thunks"
import DescriptorRow from "../DescriptorRow/DescriptorRow"

import styles from "./status-logs-modal.module.less"

// Backend times are "YYYY-MM-DD HH:mm:ss", UTC, no offset (§11 of the guide).
// Extend utc once at module load - safe if some other file also extended it.
dayjs.extend(utc)

const { Text } = Typography

// Structured events (§9.1) into the human-readable sentences the merchant reads
// on the row. Backend never sends rendered strings — the FE composes them per
// §10.
const formatEvent = (log, t) => {
  const actor = log.actor_type === "user" && log.actor_name ? log.actor_name : t("System")

  if (log.event_type === "descriptor_created") {
    const status = t(STATUS_META[log.new_value]?.label || log.new_value)
    return t("Descriptor created _status_").replace("_status_", status)
  }
  if (log.event_type === "status_changed") {
    const status = t(STATUS_META[log.new_value]?.label || log.new_value)
    return t("Status changed to _status_ by _actor_")
      .replace("_status_", status)
      .replace("_actor_", actor)
  }
  if (log.event_type === "match_type_changed") {
    const match = t(MATCH_TYPE_LABEL[log.new_value] || log.new_value)
    return t("Match type changed to _match_ by _actor_")
      .replace("_match_", match)
      .replace("_actor_", actor)
  }
  return log.event_type
}

const formatTimestamp = (raw) => {
  if (!raw) return ""
  // Parse as UTC explicitly - the API string carries no offset, so a naive
  // Date() would treat it as local and shift every timestamp.
  return dayjs.utc(raw).format("DD MMM YYYY h:mm A [(UTC)]")
}

const StatusLogsModal = ({ row, onClose, businessAccountId }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isMobile } = useSelector((state) => state.nav)

  const open = !!row
  const recordId = row?.record_id

  const { data, fetching, error } = useSelector(selectStatusLogs(recordId))

  useEffect(() => {
    if (!open || !recordId) return
    dispatch(getStatusLogs({ recordId, businessAccountId }))
  }, [open, recordId, businessAccountId, dispatch])

  const logs = data?.logs || []

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={442}
      centered={!isMobile}
      rootClassName={styles.root}
      data-testid="deflect-status-logs-modal"
      title={
        <Flex vertical gap={4}>
          <Text className={styles.title}>{t("Status logs")}</Text>
          <Text type="secondary" className={styles.subtitle}>
            {t("View the latest activity for this descriptor.")}
          </Text>
        </Flex>
      }
      footer={
        <Button type="primary" onClick={onClose} data-testid="deflect-status-logs-close-btn">
          {t("Got it")}
        </Button>
      }
    >
      {row ? <DescriptorRow row={row} hideLogs /> : null}

      {fetching ? (
        <div className={styles.centered}>
          <Spin />
        </div>
      ) : error ? (
        // Per §3 the 404 is deliberately generic — one message covers "does
        // not exist", "belongs to another BA", "archived", "not a UUID" etc.
        <div className={styles.centered}>{t("This record's history is not available.")}</div>
      ) : (
        <div className={styles.table}>
          <div className={`${styles.tableRow} ${styles.tableHeader}`}>
            <Text type="secondary" strong>
              {t("Event")}
            </Text>
            <Text type="secondary" strong>
              {t("Date and time")}
            </Text>
          </div>

          {logs.length === 0 ? (
            // §9.2 - records older than logging exist with an empty history.
            // Render an empty state, not an error.
            <div className={styles.emptyRow}>{t("No history for this descriptor yet.")}</div>
          ) : (
            logs.map((log) => (
              <div key={log.id} className={styles.tableRow}>
                <Text strong>{formatEvent(log, t)}</Text>
                <Text strong>{formatTimestamp(log.created_at)}</Text>
              </div>
            ))
          )}
        </div>
      )}
    </Modal>
  )
}

export default StatusLogsModal
